import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { Inbox, CheckCircle, BarChart3, Trash2, ArrowRight, ShieldCheck, Route, TrendingUp, ArrowUpRight, Clock, Zap } from 'lucide-react';
import { useUser } from '../contexts/UserContext';

const HomePage = () => {
  const { accounts } = useMsal();
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const userName = accounts[0]?.name?.split(' ')[0] || 'User';

  const cards = [
    {
      title: 'Pending Tickets',
      description: 'Review and approve incoming tickets requiring attention.',
      icon: <Inbox size={24} className="text-amber-500" />,
      path: '/pending',
      color: 'bg-white dark:bg-surface-dark border-slate-200 dark:border-slate-800 hover:border-amber-200 dark:hover:border-amber-500/30'
    },
    {
      title: 'Pushed Tickets',
      description: 'View history of tickets successfully pushed to Acumatica.',
      icon: <CheckCircle size={24} className="text-emerald-500" />,
      path: '/pushed',
      color: 'bg-white dark:bg-surface-dark border-slate-200 dark:border-slate-800 hover:border-emerald-200 dark:hover:border-emerald-500/30'
    },
    {
      title: 'Ticket Summary',
      description: 'Analyze reconciliation data and cover sheet summaries.',
      icon: <BarChart3 size={24} className="text-blue-500" />,
      path: '/summary',
      color: 'bg-white dark:bg-surface-dark border-slate-200 dark:border-slate-800 hover:border-blue-200 dark:hover:border-blue-500/30'
    },
    {
      title: 'Deleted Tickets',
      description: 'Access archive of removed or rejected tickets.',
      icon: <Trash2 size={24} className="text-slate-500" />,
      path: '/deleted',
      color: 'bg-white dark:bg-surface-dark border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
    }
  ];

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-background-dark p-8">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Hero Section */}
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
            Welcome back, {userName}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 max-w-2xl">
            Manage your document approvals, review reconciliation summaries, and track ticket status all in one place.
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-6 rounded-2xl bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-800 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-lg">
                <TrendingUp size={20} />
              </div>
              <span className="flex items-center gap-1 text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-2 py-1 rounded-full">
                <ArrowUpRight size={14} />
                12.5%
              </span>
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white">1,248</div>
            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-1">Monthly Volume</div>
          </div>

          <div className="p-6 rounded-2xl bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-800 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 rounded-lg">
                <Clock size={20} />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white">14</div>
            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-1">Pending Tickets</div>
          </div>

          <div className="p-6 rounded-2xl bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-800 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-lg">
                <Zap size={20} />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white">4.2 Hrs</div>
            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-1">Avg. Time to Push</div>
          </div>
        </div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cards.map((card) => (
            <div
              key={card.path}
              onClick={() => navigate(card.path)}
              className={`group relative p-6 rounded-2xl border shadow-sm transition-all duration-200 cursor-pointer hover:shadow-md ${card.color}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 group-hover:scale-110 transition-transform duration-200">
                  {card.icon}
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 -translate-x-2 group-hover:translate-x-0">
                  <ArrowRight size={20} className="text-slate-400" />
                </div>
              </div>
              
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 group-hover:text-primary transition-colors">
                {card.title}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                {card.description}
              </p>
            </div>
          ))}
        </div>

        {/* Admin Section (Conditional) */}
        {isAdmin && (
          <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">Admin Tools</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <button 
                onClick={() => navigate('/admin/users')}
                className="flex items-center gap-3 p-4 rounded-xl bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-800 hover:border-primary/50 hover:shadow-sm transition-all text-left"
              >
                <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <div className="font-bold text-slate-900 dark:text-white text-sm">User Management</div>
                  <div className="text-xs text-slate-500">Manage permissions</div>
                </div>
              </button>
              <button 
                onClick={() => navigate('/admin/mappings')}
                className="flex items-center gap-3 p-4 rounded-xl bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-800 hover:border-primary/50 hover:shadow-sm transition-all text-left"
              >
                <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400">
                  <Route size={20} />
                </div>
                <div>
                  <div className="font-bold text-slate-900 dark:text-white text-sm">Data Mappings</div>
                  <div className="text-xs text-slate-500">Configure rules</div>
                </div>
              </button>
              <button 
                onClick={() => navigate('/scans/1/1')}
                className="flex items-center gap-3 p-4 rounded-xl bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-800 hover:border-primary/50 hover:shadow-sm transition-all text-left"
              >
                <div className="p-2 rounded-lg bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400">
                  <Zap size={20} />
                </div>
                <div>
                  <div className="font-bold text-slate-900 dark:text-white text-sm">Scans</div>
                  <div className="text-xs text-slate-500">Process new scans</div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;